/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import CategoryTile from "@monsma/App/Components/CategoryTile/CategoryTile";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    selectedCategoryIds = "selectedCategoryIds",
    typeOfCategoriesToDisplay = "typeOfCategoriesToDisplay",
    titleVariant = "titleVariant",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.selectedCategoryIds]: string[];
        [fields.title]: string;
        [fields.typeOfCategoriesToDisplay]: string;
        [fields.titleVariant]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const categoriesDataView = getCategoriesDataView(state, { maxDepth: 3 });
    return {
        categoriesDataView,
        shouldLoadCategories: !categoriesDataView.value && !categoriesDataView.isLoading,
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ShopByCategoryStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    categoryGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
}

export const shopByCategoryStyles: ShopByCategoryStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        color: "primary.main",
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 15,
        css: css`
            > div {
              row-gap: 10px;
            }
        `,
    },
    categoryGridItem: {
        width: [12, 6, 4, 4, 2],
    },
    titleText: {
        css: css`
            line-height: 34px;
            margin-bottom: 25px;

            > * {
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
};

const styles = shopByCategoryStyles;

const ShopByCategoryWidget: React.FC<Props> = ({
    fields,
    categoriesDataView,
    shouldLoadCategories,
    loadCategories,
}) => {
    const [categories, setCategories] = React.useState([]);
    const [noCategoriesFound, setNoCategoriesFound] = React.useState(false);

    const filterTheCategories = () => {
        let tempCategories: any = [];

        tempCategories.length = 0;
        setNoCategoriesFound(false);

        if (categoriesDataView?.value?.length) {
            if (fields.typeOfCategoriesToDisplay === "featuredCategories") {
                tempCategories = categoriesDataView.value.filter((cat: any) => {
                    return cat["isFeatured"] === true;
                });
            } else if (fields?.selectedCategoryIds?.length) {
                categoriesDataView.value.forEach((cat: any) => {
                    fields.selectedCategoryIds.indexOf(cat.id) > -1 && tempCategories.push(cat);
                });
            }
        }

        !tempCategories.length && setNoCategoriesFound(true);
        setCategories(tempCategories.slice(0, 12));
    };

    // to avoid memory leak, put the condition inside the useEffect to limit the number of rendering
    React.useEffect(() => {
        if (shouldLoadCategories) {
            loadCategories({ maxDepth: 3 });
        }
    }, []);

    React.useEffect(() => {
        filterTheCategories();
    }, [categoriesDataView, fields.selectedCategoryIds, fields.typeOfCategoriesToDisplay]);

    if (categoriesDataView?.isLoading || (!categories.length && !noCategoriesFound)) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    const title = fields.title;

    return (
        <>
            {title && <StyledWrapper {...styles.titleText}>{parse(title, parserOptions)}</StyledWrapper>}
            {categories.length > 0 && (
                <GridContainer {...styles.container}>
                    {categories.map(category => (
                        <GridItem {...styles.categoryGridItem} key={uniqueId()}>
                            <CategoryTile
                                category={category}
                                showImages={true}
                                data-test-selector={`categoriesLink_${uniqueId()}`}
                                fullImage={true}
                                titleVariant={fields.titleVariant}
                            />
                        </GridItem>
                    ))}
                </GridContainer>
            )}
        </>
    );
};

const definition: WidgetDefinition = {
    group: "Basic",
    displayName: "Shop By Category",
    fieldDefinitions: [
        {
            name: fields.title,
            displayName: "Title",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "General",
        },
        {
            name: fields.typeOfCategoriesToDisplay,
            displayName: "Categories To Display",
            editorTemplate: "RadioButtonsField",
            tooltip: "Only first 18 categories will be showing",
            options: [
                {
                    displayName: "Featured Categories",
                    value: "featuredCategories",
                },
                {
                    displayName: "Select Categories",
                    value: "selectCategories",
                },
            ],
            isRequired: true,
            defaultValue: "",
            fieldType: "General",
        },
        {
            name: fields.selectedCategoryIds,
            displayName: "Select Categories",
            editorTemplate: "CategoriesField",
            defaultValue: [],
            fieldType: "General",
            isRequired: true,
            isVisible: widget => widget.fields.typeOfCategoriesToDisplay === "selectCategories",
        },
        {
            name: fields.titleVariant,
            displayName: "Category Title Variant",
            editorTemplate: "DropDownField",
            options: [
                { value: "h1", displayName: "H1" },
                { value: "h2", displayName: "H2" },
                { value: "h3", displayName: "H3" },
                { value: "h4", displayName: "H4" },
                { value: "h5", displayName: "H5" },
                { value: "h6", displayName: "H6" },
            ],
            defaultValue: "h3",
            fieldType: "General",
            isRequired: true,
            sortOrder: 17,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShopByCategoryWidget),
    definition,
};

export default widgetModule;
